export const PLUGIN_TYPES = ['PRETRAIN', 'PREPROCESS', 'TRAIN', 'EVALUATE', 'POSTPROCESS'];

export const PLUGIN_COLORS = {
  PRETRAIN:    'b-purple',
  PREPROCESS:  'b-blue',
  TRAIN:       'b-or',
  EVALUATE:    'b-green',
  POSTPROCESS: 'b-amber',
};

export const PLUGIN_DESCRIPTIONS = {
  PRETRAIN:    'transform(df, config, work_dir) → dict with adapted_model_path',
  PREPROCESS:  'transform(df, config) → df',
  TRAIN:       'train(df, config, params, work_dir) → None',
  EVALUATE:    'evaluate(model, X_test, y_test, config) → dict',
  POSTPROCESS: 'postprocess(result, config) → dict',
};

export const PLUGIN_TEMPLATES = {
  PREPROCESS: `# aifab - PREPROCESS plugin
# Contract: transform(df, config) -> pd.DataFrame
# Receives the raw training DataFrame. Return cleaned DataFrame.

import pandas as pd

def transform(df: pd.DataFrame, config: dict) -> pd.DataFrame:
    """
    Pre-process the training data before model training.
    
    Args:
        df:     Raw training DataFrame loaded from the datasource.
        config: Feature config snapshot (target_column, feature_columns, etc.)
    
    Returns:
        Cleaned DataFrame. Must preserve the target column if present.
    """
    text_cols = [k for k, v in (config.get('feature_columns') or {}).items()
                 if v in ('text', 'feature')]
    
    # Example: strip whitespace and lowercase text columns
    for col in text_cols:
        if col in df.columns:
            df[col] = df[col].astype(str).str.strip().str.lower()
    
    # Example: drop rows with missing target
    target = config.get('target_column')
    if target and target in df.columns:
        df = df.dropna(subset=[target]).reset_index(drop=True)
    
    return df
`,

  EVALUATE: `# aifab - EVALUATE plugin
# Contract: evaluate(model, X_test, y_test, config) -> dict
# Return a metrics dict. Keys become run metrics.

def evaluate(model, X_test, y_test, config: dict) -> dict:
    """
    Custom evaluation logic.
    
    Args:
        model:   The trained model object (sklearn, transformers, etc.)
        X_test:  Test features
        y_test:  True labels
        config:  Feature config snapshot
    
    Returns:
        Dict of metric name -> value. All values must be JSON-serialisable.
    """
    from sklearn.metrics import classification_report, f1_score, accuracy_score
    
    y_pred = model.predict(X_test)
    
    return {
        "f1_score":   round(f1_score(y_test, y_pred, average="weighted"), 4),
        "accuracy":   round(accuracy_score(y_test, y_pred), 4),
        "report":     classification_report(y_test, y_pred),
    }
`,

  POSTPROCESS: `# aifab - POSTPROCESS plugin
# Contract: postprocess(result, config) -> dict
# Receives the result dict from training. Return enriched dict.

def postprocess(result: dict, config: dict) -> dict:
    """
    Post-process the training result.
    
    Args:
        result: Result dict from training (metrics, artifact paths, etc.)
        config: Feature config snapshot
    
    Returns:
        Enriched result dict. Must include all fields from input result.
    """
    # Example: add a human-readable summary
    f1 = result.get('f1_score', 0)
    result['quality'] = 'good' if f1 >= 0.80 else 'fair' if f1 >= 0.65 else 'poor'
    return result
`,

  TRAIN: `# aifab - CUSTOM TRAIN plugin
# Full training override. Platform provides data; you control training.
# Must write result.json to WORK_DIR before exit.
#
# Environment variables available:
#   WORK_DIR          - temp dir for this job (write result.json here)
#   DATA_PATH         - path to the training CSV
#   MODEL_LOCAL_PATH  - base model (HuggingFace name or local path)
#   JOB_ID            - unique job identifier
#   HAS_GPU           - "true" or "false"
#   HEARTBEAT_PATH    - write PID here periodically to avoid timeout

import os
import json
from pathlib import Path

WORK_DIR   = os.environ["WORK_DIR"]
DATA_PATH  = os.environ["DATA_PATH"]
MODEL_PATH = os.environ["MODEL_LOCAL_PATH"]
HAS_GPU    = os.environ.get("HAS_GPU", "false").lower() == "true"
HEARTBEAT  = Path(WORK_DIR) / "heartbeat"

def write_heartbeat():
    HEARTBEAT.write_text(str(os.getpid()))

# ── Load data ─────────────────────────────────────────────────────────────────
import pandas as pd
df = pd.read_csv(DATA_PATH)
write_heartbeat()

# TODO: implement training logic
# ...

# ── Write result.json (REQUIRED) ──────────────────────────────────────────────
# Platform reads this file to store metrics and artifact paths.
result = {
    "f1_score":    0.0,    # Classification: weighted F1
    "accuracy":    0.0,    # Classification: accuracy
    "artifact_path": str(Path(WORK_DIR) / "model"),
    # Add any custom metrics here
}
Path(WORK_DIR, "result.json").write_text(json.dumps(result))
write_heartbeat()
print("Training complete.")
`,

  // NER template - shown when TRAIN plugin type is selected for NER runs
  NER: `# =============================================================================
# aifab NER Training Template - v1.0
# Named Entity Recognition using BERT token classification
#
# DATASET FORMAT:
#   Your CSV must have:
#     text_column  - raw sentence/document
#     tags_column  - space-separated IOB tags, one per token
#
#   IOB format:  O B-TYPE I-TYPE
#   Example:
#     text: "Apple is based in Cupertino California"
#     tags: "B-ORG O O O B-LOC I-LOC"
#
# RESULT CONTRACT (required):
#   {
#     "overall_f1":       <float>,
#     "per_entity":       {"TYPE": {"f1": 0.91, "precision": 0.90, "recall": 0.92}},
#     "model_path":       "<path to saved model>",
#     "label_list":       ["O", "B-PER", ...],
#     "training_samples": <int>
#   }
# =============================================================================

import os, json
import numpy as np
import pandas as pd
from pathlib import Path

WORK_DIR   = os.environ["WORK_DIR"]
DATA_PATH  = os.environ["DATA_PATH"]
MODEL_PATH = os.environ["MODEL_LOCAL_PATH"]
HAS_GPU    = os.environ.get("HAS_GPU","false").lower() == "true"
HEARTBEAT  = Path(WORK_DIR) / "heartbeat"

def write_heartbeat(): HEARTBEAT.write_text(str(os.getpid()))

# TODO: Set your column names
TEXT_COLUMN    = "description"
ENTITIES_COLUMN = "ner_tags"

# TODO: Define your entity types (without B-/I- prefix)
ENTITY_TYPES = ["PER", "ORG", "LOC", "MISC"]

LABEL_LIST = ["O"] + [f"B-{t}" for t in ENTITY_TYPES] + [f"I-{t}" for t in ENTITY_TYPES]
LABEL2ID   = {l: i for i, l in enumerate(LABEL_LIST)}
ID2LABEL   = {i: l for i, l in enumerate(LABEL_LIST)}

EPOCHS      = int(os.environ.get("EPOCHS", "3"))
BATCH_SIZE  = int(os.environ.get("BATCH_SIZE", "16"))
LR          = float(os.environ.get("LEARNING_RATE", "5e-5"))
MAX_SEQ_LEN = int(os.environ.get("MAX_SEQ_LENGTH", "128"))
TRAIN_SPLIT = float(os.environ.get("TRAIN_SPLIT", "0.8"))

# Load data
print(f"[NER] Loading {DATA_PATH}")
df = pd.read_csv(DATA_PATH).dropna(subset=[TEXT_COLUMN, ENTITIES_COLUMN])
texts     = df[TEXT_COLUMN].tolist()
tag_seqs  = [str(t).split() for t in df[ENTITIES_COLUMN].tolist()]
split_idx = int(len(df) * TRAIN_SPLIT)
write_heartbeat()

from transformers import (
    AutoTokenizer, AutoModelForTokenClassification,
    TrainingArguments, Trainer, DataCollatorForTokenClassification,
)
import torch
from torch.utils.data import Dataset

tokeniser = AutoTokenizer.from_pretrained(MODEL_PATH)
model     = AutoModelForTokenClassification.from_pretrained(
    MODEL_PATH, num_labels=len(LABEL_LIST),
    id2label=ID2LABEL, label2id=LABEL2ID, ignore_mismatched_sizes=True,
)

def encode(texts, tags):
    enc = tokeniser([t.split() for t in texts], truncation=True, max_length=MAX_SEQ_LEN,
                    padding="max_length", is_split_into_words=True, return_tensors="pt")
    labels = []
    for i, tag_seq in enumerate(tags):
        word_ids = enc.word_ids(i); lbl = []; prev = None
        for w in word_ids:
            lbl.append(-100 if w is None else LABEL2ID.get(tag_seq[w], 0) if w != prev else -100)
            prev = w
        labels.append(lbl)
    enc["labels"] = torch.tensor(labels)
    return enc

class NERDataset(Dataset):
    def __init__(self, enc):
        self.enc = enc
    def __len__(self):  return self.enc["input_ids"].shape[0]
    def __getitem__(self, i): return {k: v[i] for k, v in self.enc.items()}

train_enc = encode(texts[:split_idx], tag_seqs[:split_idx])
eval_enc  = encode(texts[split_idx:], tag_seqs[split_idx:])
write_heartbeat()

def compute_metrics(p):
    preds = np.argmax(p.predictions, -1)
    true  = [[ID2LABEL[l] for l in row if l != -100] for row in p.label_ids]
    pred  = [[ID2LABEL[pr] for pr, l in zip(pr_row, la_row) if l != -100]
             for pr_row, la_row in zip(preds, p.label_ids)]
    try:
        from seqeval.metrics import f1_score
        return {"f1": f1_score(true, pred, average="weighted")}
    except ImportError:
        correct = sum(p==t for ps,ts in zip(pred,true) for p,t in zip(ps,ts))
        total   = sum(len(t) for t in true)
        return {"accuracy": correct/total if total else 0}

output_dir = str(Path(WORK_DIR) / "ner_model")
args = TrainingArguments(
    output_dir=output_dir, num_train_epochs=EPOCHS,
    per_device_train_batch_size=BATCH_SIZE, per_device_eval_batch_size=BATCH_SIZE,
    learning_rate=LR, evaluation_strategy="epoch", save_strategy="epoch",
    load_best_model_at_end=True, no_cuda=not HAS_GPU, fp16=HAS_GPU,
    logging_dir=str(Path(WORK_DIR)/"logs"), report_to=[],
)
trainer = Trainer(
    model=model, args=args,
    train_dataset=NERDataset(train_enc), eval_dataset=NERDataset(eval_enc),
    tokenizer=tokeniser, data_collator=DataCollatorForTokenClassification(tokeniser),
    compute_metrics=compute_metrics,
)
trainer.train()
write_heartbeat()

model_save = str(Path(WORK_DIR) / "saved_model")
trainer.save_model(model_save)
tokeniser.save_pretrained(model_save)

# Per-entity metrics
per_entity = {}
try:
    from seqeval.metrics import classification_report, f1_score
    pout   = trainer.predict(NERDataset(eval_enc))
    pids   = np.argmax(pout.predictions, -1)
    true_s = [[ID2LABEL[l] for l in r if l != -100] for r in pout.label_ids]
    pred_s = [[ID2LABEL[p] for p,l in zip(pr,la) if l != -100] for pr,la in zip(pids,pout.label_ids)]
    report = classification_report(true_s, pred_s, output_dict=True)
    f1     = f1_score(true_s, pred_s, average="weighted")
    for t in ENTITY_TYPES:
        if t in report:
            per_entity[t] = {"f1": round(report[t]["f1-score"],4),
                              "precision": round(report[t]["precision"],4),
                              "recall": round(report[t]["recall"],4)}
except Exception:
    f1 = 0.0

# Write result.json
result = {
    "overall_f1":       round(float(f1), 4),
    "per_entity":       per_entity,
    "model_path":       model_save,
    "label_list":       LABEL_LIST,
    "training_samples": split_idx,
    "eval_samples":     len(texts) - split_idx,
}
Path(WORK_DIR, "result.json").write_text(json.dumps(result, indent=2))
write_heartbeat()
print(f"[NER] Done. overall_f1={result['overall_f1']}")
`,
};
